import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useAuthModal } from '../../auth/AuthModalContext';
import { CLARITY_GOLD, CLARITY_GOLD_TEXT, FONT_DISPLAY, FONT_MONO } from '../../constants/brand';

// ── Terminal signature element ──────────────────────────────────────────────
// Types out a self-doubt, then answers it plainly. This is the one bold
// element on the page — it makes the brief's actual thesis ("for people who
// thought IT wasn't for them") something you watch happen, not a claim.

const PROMPTS = [
  { doubt: 'Never written a line of code?', answer: 'Start anyway.' },
  { doubt: 'No degree for this?', answer: 'Not required.' },
  { doubt: 'Think you started too late?', answer: "You haven't started yet." },
  { doubt: "Worried it won't click?", answer: 'It clicks. Eventually.' },
];

const TYPE_SPEED = 38;
const LINE_PAUSE = 350;
const HOLD_TIME = 1800;

function useTerminalPrompts(prompts) {
  const [promptIndex, setPromptIndex] = useState(0);
  const [doubtText, setDoubtText] = useState('');
  const [answerText, setAnswerText] = useState('');
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  useEffect(() => {
    const { doubt, answer } = prompts[promptIndex];

    if (prefersReducedMotion) {
      setDoubtText(doubt);
      setAnswerText(answer);
      return undefined;
    }

    setDoubtText('');
    setAnswerText('');

    const timeouts = [];
    let doubtChars = 0;
    let answerChars = 0;

    const advance = () => {
      setPromptIndex((current) => (current + 1) % prompts.length);
    };

    const typeAnswer = () => {
      answerChars += 1;
      setAnswerText(answer.slice(0, answerChars));
      if (answerChars < answer.length) {
        timeouts.push(setTimeout(typeAnswer, TYPE_SPEED));
      } else {
        timeouts.push(setTimeout(advance, HOLD_TIME));
      }
    };

    const typeDoubt = () => {
      doubtChars += 1;
      setDoubtText(doubt.slice(0, doubtChars));
      if (doubtChars < doubt.length) {
        timeouts.push(setTimeout(typeDoubt, TYPE_SPEED));
      } else {
        timeouts.push(setTimeout(typeAnswer, LINE_PAUSE));
      }
    };

    timeouts.push(setTimeout(typeDoubt, TYPE_SPEED));

    return () => timeouts.forEach(clearTimeout);
  }, [promptIndex, prompts, prefersReducedMotion]);

  return { doubtText, answerText };
}

const BlinkingCursor = ({ color = 'currentColor' }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      display: 'inline-block',
      width: '0.5em',
      height: '1em',
      ml: '2px',
      bgcolor: color,
      verticalAlign: 'text-bottom',
      animation: 'qc-cursor-blink 1s steps(1) infinite',
      '@keyframes qc-cursor-blink': {
        '0%, 49%': { opacity: 1 },
        '50%, 100%': { opacity: 0 },
      },
      '@media (prefers-reduced-motion: reduce)': {
        animation: 'none',
      },
    }}
  />
);

// ── Hero ─────────────────────────────────────────────────────────────────────

const HeroSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { openAuthModal } = useAuthModal();
  const { doubtText, answerText } = useTerminalPrompts(PROMPTS);

  const handleHowItWorksClick = useCallback((event) => {
    const target = document.getElementById('how-it-works');
    if (target) {
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // Hero is rendered outside the home page (or section hasn't mounted yet) —
      // fall back to a real navigation so the link still works.
      event.preventDefault();
      navigate('/#how-it-works');
    }
  }, [navigate]);

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        pt: { xs: 6, sm: 10, md: 14 },
        pb: { xs: 8, sm: 12 },
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${alpha(theme.palette.primary.main, 0.07)}, transparent)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="md">
        <Stack alignItems="center" spacing={4} textAlign="center">
          <Chip
            label="For people who thought IT wasn't for them"
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontWeight: 600, fontFamily: FONT_MONO, fontSize: '0.72rem', letterSpacing: 0.3 }}
          />

          <Box>
            <Typography
              component="h1"
              variant="h2"
              sx={{
                fontFamily: FONT_DISPLAY,
                fontWeight: 800,
                lineHeight: 1.15,
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.75rem', md: '3.25rem' },
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              IT skills, explained like you're new — because you might be
            </Typography>

            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 640, mx: 'auto', lineHeight: 1.7, fontWeight: 400 }}
            >
              Qode Clarity teaches real, usable IT skills from a complete standing start.
              No jargon. No assumed knowledge. Just plain explanations, practical tasks,
              and a path that actually makes sense.
            </Typography>
          </Box>

          {/* Terminal signature — decorative; meaning is restated below for screen readers */}
          <Box
            aria-hidden
            sx={{
              fontFamily: FONT_MONO,
              bgcolor: alpha(theme.palette.text.primary, 0.03),
              border: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
              borderRadius: 2,
              px: 3,
              py: 2.25,
              width: { xs: '100%', sm: 440 },
              textAlign: 'left',
            }}
          >
            <Typography component="p" sx={{ fontFamily: 'inherit', fontSize: '0.92rem', color: 'text.primary', mb: 0.75, minHeight: '1.4em' }}>
              <Box component="span" sx={{ color: alpha(theme.palette.text.primary, 0.4) }}>{'> '}</Box>
              {doubtText}
              {!answerText && <BlinkingCursor />}
            </Typography>
            <Typography component="p" sx={{ fontFamily: 'inherit', fontSize: '0.92rem', color: CLARITY_GOLD_TEXT, fontWeight: 600, minHeight: '1.4em' }}>
              {answerText && (
                <>
                  <Box component="span" sx={{ opacity: 0.6 }}>{'# '}</Box>
                  {answerText}
                  <BlinkingCursor color={CLARITY_GOLD} />
                </>
              )}
            </Typography>
          </Box>
          <Typography sx={visuallyHidden}>
            You don't need experience, a degree, or perfect timing to start. Qode Clarity
            meets you exactly where you are.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            {user ? (
              <Button
                component={RouterLink}
                to="/my-learning"
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{ px: 4, fontWeight: 700, fontSize: '1rem' }}
              >
                Go to my learning
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => openAuthModal('register')}
                  sx={{ px: 4, fontWeight: 700, fontSize: '1rem' }}
                >
                  Create account — it's free
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => openAuthModal('login')}
                  sx={{ px: 4, fontWeight: 600, fontSize: '1rem' }}
                >
                  Sign in
                </Button>
              </>
            )}
            <Button
              component="a"
              href="#how-it-works"
              onClick={handleHowItWorksClick}
              variant="text"
              size="large"
              sx={{ px: 3, fontWeight: 600, fontSize: '1rem', color: 'text.secondary' }}
            >
              How it works
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default HeroSection;
